import { motion } from "framer-motion";
import { AddToBag, SoldOutBadge } from "@/components/AddToBag";
import { discountPct, formatINR, productUrl } from "@/data/catalog";
import { useLiveMeta, useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

export const ProductCard = ({ p: base, i, className = "", testPrefix = "product" }) => {
  const p = useLiveProduct(base);
  const { sale } = useLiveMeta();
  const off = discountPct(p.price, p.compare);
  // Discount titles are often internal codes; show the operator-facing label only when it is a real sale name.
  const saleLabel = p.saleTitle && sale?.label && sale.label !== "Sale" ? sale.label : null;
  return (
    <motion.article
      data-testid={`${testPrefix}-card-${p.id}`}
      data-name={p.name}
      data-price={p.price}
      data-variant={p.variant}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay: (i % 4) * 0.07 }}
      className={`group ${className}`}
    >
      <a href={productUrl(p.handle)} data-testid={`${testPrefix}-link-${p.id}`} className="relative block aspect-[4/5] overflow-hidden rounded-[2px] bg-card">
        <img src={p.img} alt={`${p.name} ${p.note}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
        {p.alt && (
          <img src={p.alt} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
        )}
        <span className="absolute left-3 top-3 font-mono text-xs text-white mix-blend-difference">{String(i + 1).padStart(2, "0")}</span>
        <span className="absolute right-3 top-3 flex gap-2">
          <SoldOutBadge p={p} />
          {off > 0 && (
            <span data-testid={`${testPrefix}-discount-${p.id}`} className="rounded-full bg-ink/80 px-2.5 py-1 font-mono text-[11px] text-champagne backdrop-blur-md">
              −{off}%{saleLabel ? ` · ${saleLabel}` : ""}
            </span>
          )}
        </span>
      </a>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-xl leading-tight text-cream sm:text-2xl">{p.name}</h3>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.14em] text-stone">{p.note}</p>
        </div>
        <div className="shrink-0 text-right">
          <p data-testid={`${testPrefix}-price-${p.id}`} data-live={p.live} className="text-base text-cream">{formatINR(p.price)}</p>
          {off > 0 && <p className="text-xs text-stone line-through">{formatINR(p.compare)}</p>}
        </div>
      </div>
      <AddToBag p={p} testId={`${testPrefix}-add-${p.id}`} className="btn-bag mt-4" />
    </motion.article>
  );
};
